import './OrganisationUserPanel.scss'
import { deriveRoleNamesFromRoleNumber } from '@/util'
import { createTableColumn, TableCellLayout, TableColumnDefinition } from '@fluentui/react-components'
import { Person24Regular } from '@fluentui/react-icons'
import { useState } from 'react'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useGetSearchParam } from '@/hooks'
import { DataGridComponent } from '../../DataGrid/DataGridComponent'
import { AddUserToOrgForm } from '../../Forms/AddUserToOrgForm'
import { OverlayDrawerComponent } from '../../OverlayDrawerComponent/OverlayDrawerComponent'
import { RowActionsMenu } from './RowActionsMenu'

type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

export const OrganisationUserPanel = () => {
  const { getSubscriptionsApiListOrganisationUsers } = createRestClient()

  const orgId = useGetSearchParam<number>('orgId')

  const {
    data: organisationUsers,
    isPending,
    isError,
    refetch,
  } = getSubscriptionsApiListOrganisationUsers.useQuery(
    {
      query: {
        orgId,
      },
    },
    { enabled: !!orgId }
  )

  const [isAddOpen, setIsAddOpen] = useState<boolean>(false)

  // Todo: Add error handling and loading state
  if (isError) return null
  if (isPending) return null

  const columns: TableColumnDefinition<MessagesSubscriptionsOrganisationUser>[] = [
    createTableColumn<MessagesSubscriptionsOrganisationUser>({
      columnId: 'givenName',
      compare: (a, b) => a.givenName.localeCompare(b.givenName),
      renderHeaderCell: () => 'Given name',
      renderCell: (item) => (
        <TableCellLayout
          media={
            <Person24Regular
              className={
                item.isFrozen ? 'person-icon-frozen' : item.active ? 'person-icon-active' : 'person-icon-inactive'
              }
            />
          }
        >
          <div className="user-info">
            <span className="user-info__name">{`${item.givenName} ${item.familyName}`}</span>
            <span className="user-info__email">{item.email}</span>
          </div>
        </TableCellLayout>
      ),
    }),
    createTableColumn<MessagesSubscriptionsOrganisationUser>({
      columnId: 'roles',
      compare: (a, b) => a.roles - b.roles,
      renderHeaderCell: () => 'Roles',
      renderCell: (item) => (
        <TableCellLayout>
          <div className="roles-container">
            {deriveRoleNamesFromRoleNumber(item.roles).map((role) => (
              <div className="role-badge" key={`${item.id}-${role}`}>
                {role}
              </div>
            ))}
          </div>
        </TableCellLayout>
      ),
    }),
    createTableColumn<MessagesSubscriptionsOrganisationUser>({
      columnId: 'actions',
      renderHeaderCell: () => 'Actions',
      renderCell: (item) => <RowActionsMenu user={item} refetchOrganisationUsers={refetch} />,
    }),
  ]

  return (
    <>
      <DataGridComponent
        items={organisationUsers}
        columns={columns}
        heightAdjustment={90}
        onAdd={() => setIsAddOpen(true)}
      />
      <OverlayDrawerComponent
        open={isAddOpen}
        setOpen={setIsAddOpen}
        title="Add user to organisation"
        content={<AddUserToOrgForm onSubmit={() => setIsAddOpen(false)} refetchOrganisationUsers={refetch} />}
      />
    </>
  )
}
